/**
 * validators.js — Validações para o Brasil, com regras regionais para o Rio de Janeiro.
 *
 * DDDs do Estado do Rio de Janeiro:
 *   21 → Capital + Baixada Fluminense
 *   22 → Norte Fluminense, Campos dos Goytacazes
 *   24 → Sul Fluminense, Petrópolis, Serra
 */

const RJ_DDDS = ["21", "22", "24"];

// ─── CPF ────────────────────────────────────────────────────────────────────

/**
 * Valida CPF brasileiro (11 dígitos, com verificação de dígitos verificadores).
 * @param {string} cpf — pode conter máscara (pontos/traço)
 * @returns {boolean}
 */
export const validateCPF = (cpf) => {
  const digits = cpf.replace(/[^\d]/g, "");
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let rem = (sum * 10) % 11;
  if (rem === 10 || rem === 11) rem = 0;
  if (rem !== parseInt(digits[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  rem = (sum * 10) % 11;
  if (rem === 10 || rem === 11) rem = 0;
  return rem === parseInt(digits[10]);
};

// ─── Telefone ────────────────────────────────────────────────────────────────

/**
 * Valida telefone brasileiro (celular ou fixo).
 * Serviço restrito ao RJ: avisa se o DDD não for 21, 22 ou 24.
 *
 * @param {string} phone — pode conter máscara
 * @returns {{ valid: boolean, msg?: string, warn?: string }}
 */
export const validatePhone = (phone) => {
  const digits = phone.replace(/\D/g, "");

  if (digits.length < 10) {
    return { valid: false, msg: "Telefone incompleto. Informe DDD + número." };
  }
  if (digits.length > 11) {
    return { valid: false, msg: "Telefone com dígitos demais. Verifique o número." };
  }

  const ddd = digits.slice(0, 2);
  const dddNum = parseInt(ddd, 10);

  // DDDs válidos no Brasil: 11–99 (exclui 00, 01–10)
  if (dddNum < 11 || dddNum > 99) {
    return { valid: false, msg: `DDD inválido: ${ddd}. Informe o DDD correto.` };
  }

  // Celular: 11 dígitos — 9º dígito deve ser 9
  if (digits.length === 11 && digits[2] !== "9") {
    return { valid: false, msg: "Celular deve começar com 9 após o DDD. Ex: (21) 99999-0000." };
  }

  if (!RJ_DDDS.includes(ddd)) {
    return {
      valid: true,
      warn: `DDD ${ddd} não é do Rio de Janeiro (21, 22 ou 24). Confirme se está correto.`,
    };
  }

  return { valid: true };
};

// ─── CEP ─────────────────────────────────────────────────────────────────────

/**
 * Valida se o CEP tem exatamente 8 dígitos.
 * @param {string} cep — pode conter máscara (00000-000)
 * @returns {boolean}
 */
export const validateCEP = (cep) => {
  return cep.replace(/\D/g, "").length === 8;
};

// ─── Data de Nascimento ───────────────────────────────────────────────────────

/**
 * Valida se o valor é uma data válida, não está no futuro e tem ano realista (>= 1900).
 * @param {string} value — formato YYYY-MM-DD (input[type=date])
 * @returns {boolean}
 */
export const validateDate = (value) => {
  if (!value) return false;
  const d = new Date(value);
  if (isNaN(d.getTime())) return false;
  if (d.getFullYear() < 1900) return false;
  return d <= new Date();
};

/**
 * Valida faixa etária a partir da data de nascimento.
 * @param {string} value — formato YYYY-MM-DD
 * @param {number} minAge — idade mínima (padrão: 18)
 * @param {number} maxAge — idade máxima (padrão: 100)
 * @returns {boolean}
 */
export const validateMinAge = (value, minAge = 18, maxAge = 100) => {
  if (!value) return false;
  const dob = new Date(value);
  if (isNaN(dob.getTime())) return false;
  const today = new Date();
  const age =
    today.getFullYear() -
    dob.getFullYear() -
    (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
  return age >= minAge && age <= maxAge;
};

/**
 * Retorna a idade calculada em anos.
 * @param {string} value — formato YYYY-MM-DD
 * @returns {number}
 */
export const calcAge = (value) => {
  if (!value) return 0;
  const dob = new Date(value);
  const today = new Date();
  return (
    today.getFullYear() -
    dob.getFullYear() -
    (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0)
  );
};

// ─── URL ─────────────────────────────────────────────────────────────────────

/**
 * Valida se o valor é uma URL válida (http/https).
 * Retorna `true` para valor vazio (campo opcional).
 * @param {string} value
 * @returns {boolean}
 */
export const validateURL = (value) => {
  if (!value || value.trim() === "") return true;
  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

// ─── E-mail ───────────────────────────────────────────────────────────────────

/**
 * Valida formato básico de e-mail.
 * @param {string} value
 * @returns {boolean}
 */
export const validateEmail = (value) => {
  if (!value || value.trim() === "") return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
};

// ─── Nome ─────────────────────────────────────────────────────────────────────

/**
 * Valida se o nome tem pelo menos 2 caracteres e contém apenas letras/espaços/acentos.
 * @param {string} value
 * @returns {boolean}
 */
export const validateName = (value) => {
  if (!value || value.trim().length < 2) return false;
  return /^[A-Za-zÀ-ÿ\s'-]+$/.test(value.trim());
};
